import { useEffect, useState } from 'react';
import { api, type CodeLibRow } from '../api/client';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Codes & Standards browser — fire-alarm + Div 27/28 reference library. Filter by
// discipline + free-text search; admins can add new entries from this screen.
const DISCIPLINES: { key: string; label: string }[] = [
  { key: '', label: 'All disciplines' },
  { key: 'fire_alarm', label: 'Fire Alarm' },
  { key: 'life_safety', label: 'Life Safety' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'telecom', label: 'Telecom / Low-Voltage' },
  { key: 'accessibility', label: 'Accessibility (ADA)' },
];

export function CodesLibrary() {
  const { t } = useLang();
  const [codes, setCodes] = useState<CodeLibRow[]>([]);
  const [discipline, setDiscipline] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try { setCodes((await api.getCodes({ discipline: discipline || undefined, q: q || undefined })).codes); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load codes', fil: 'Hindi na-load ang mga codes' })); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [discipline]);

  return (
    <div>
      <h1>{t({ en: 'Codes & Standards Library', fil: 'Codes & Standards Library' })}</h1>
      <p className="muted">{t({ en: 'Authoritative reference for fire alarm and Division 27/28 work. Use it on bids, in proposals, and for AHJ submittal references.', fil: 'Authoritative reference para sa fire alarm at Division 27/28 na trabaho. Gamitin ito sa mga bid, sa mga proposal, at para sa AHJ submittal references.' })}</p>
      {error && <div className="error">{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>{t({ en: 'Discipline', fil: 'Disiplina' })}</label>
            <select value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
              {DISCIPLINES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
            <label>{t({ en: 'Search (code / title / section / notes)', fil: 'Maghanap (code / title / section / notes)' })}</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
              placeholder="e.g. NFPA 72, §907, smoke spacing, low-frequency" />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => void load()}>{t(G.search)}</button>
        </div>
      </div>

      <div className="card">
        <h2>{codes.length} {t({ en: 'entries', fil: 'mga entry' })}</h2>
        <table>
          <thead><tr><th>Code</th><th>Section</th><th>Title</th><th>Discipline</th><th>Edition</th><th>Where</th><th>Applies to</th></tr></thead>
          <tbody>
            {codes.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.code}</a> : c.code}</strong></td>
                <td className="muted">{c.section ?? '—'}</td>
                <td>{c.title}{c.notes && <div className="muted" style={{ fontSize: 12 }}>{c.notes}</div>}</td>
                <td><span className="pill pill-proposed">{(c.discipline ?? c.level).replace('_', ' ')}</span></td>
                <td>{c.edition ?? '—'}</td>
                <td className="muted">{c.applies_in ?? c.level}</td>
                <td className="muted">{c.applies_to_system ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
